import React, { useMemo, useState } from 'react';
import { AiFillStar } from 'react-icons/ai';
import { BiMinus } from 'react-icons/bi';
import { BsFlagFill, BsPlus } from 'react-icons/bs';
import { FaChevronDown } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow, PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Authorizer from '../../../../../../../../../../../../components/Authorizer';
import StackedButtonGroup from '../../../../../../../../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import LoadingSpinner from '../../../../../../../../../../../../components/Spinner';
import { analyticsIds } from '../../../../../../../../../../../../constants/analyticsIds';
import { BulkCodeActions, CodeActions } from '../../../../../../../../../../../../constants/codes';
import { CREATE_ITEM_CODE_STATUS_APPLIED, CREATE_ITEM_CODE_STATUS_WAIVED } from '../../../../../../../../../../../../constants/permissions';
import { createToast } from '../../../../../../../../../../../../redux/modules/app';
import { showModal } from '../../../../../../../../../../../../redux/modules/modal';
import { useCompanyCodes } from '../../../../../../../../../../../../services/company-service';
import { chunkArray, createMap } from '../../../../../../../../../../../../utils/arrayUtil';

import ConfirmBulkCodeUpdateModal from './components/ConfirmBulkCodeUpdateModal';

const ItemCodesBulkUpdatePopover = ({ companyId, insuredId }) => {
  const dispatch = useDispatch();

  const { data: companyCodes, isLoading: companyCodesLoading } = useCompanyCodes(companyId);

  const checkboxes = useMemo(() => companyCodes.map(code => ({ ...code, checked: false })), [companyCodes]);

  const itemCodesMap = useMemo(() => createMap(checkboxes, 'id'), [checkboxes]);
  const [selectedCodes, setSelectedCodes] = useState(itemCodesMap);

  let chunkSize = companyCodes?.length / 2;
  if (chunkSize % 2 !== 0) {
    chunkSize += 1;
  }

  const chunkedCompanyCodes = chunkArray(companyCodes, chunkSize);

  const handleSingleCheckbox = (e, codeId) => {
    setSelectedCodes({ ...selectedCodes, [codeId]: { ...selectedCodes[codeId], checked: e.target.checked } });
  };

  const handleApplyCodes = status => {
    const checkedCodes = Object.values(selectedCodes).filter(code => code.checked);

    if (!checkedCodes.length) {
      return dispatch(createToast({
        title: 'Please select at least one code.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      }));
    }

    return dispatch(showModal(() => <ConfirmBulkCodeUpdateModal codeAction={status} codes={checkedCodes} insuredId={insuredId} />, {
      title: 'Confirm Bulk Update',
      actionRequired: false,
      size: 'lg',
      buttons: {
        component: StackedButtonGroup,
        primaryButtonOptions: {
          dataAnalyticsId: analyticsIds.insuredProfilePage.itemsComponent.bulkCodeConfirmationButton,
          label: 'Update',
          type: 'submit',
          form: 'bulkApplyCodes'
        }
      }
    }));
  };

  if (companyCodesLoading) return <LoadingSpinner />;

  return (
    <Popover>
      <PopoverTrigger>
        <IconButton
          aria-label="down-arrow-icon-button"
          icon={<Icon as={FaChevronDown} />}
          variant="unstyled"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverArrow />
          <PopoverHeader>
            <Text
              textAlign="center"
              textStyle="bodyMd500"
            >
              Select Codes
            </Text>
          </PopoverHeader>
          <PopoverBody>
            <Box
              maxH="500px"
              minW="200px"
              overflowY="auto"
              px={2}
            >
              <HStack alignItems="flex-start" spacing={6}>
                <HStack
                  alignItems="flex-start"
                  justifyContent="center"
                  px={4}
                  spacing={2}
                  textStyle="bodyMd"
                >
                  {chunkedCompanyCodes.map((codeGroup, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={`${i}-code-chunk`}>
                      {codeGroup.map(code => (
                        <Box key={code.id}>
                          <Grid
                            alignItems="baseline"
                            p={1}
                            templateColumns="repeat(4, 1fr)"
                          >
                            <GridItem alignItems="center" colSpan={2}>
                              <Checkbox
                                isChecked={selectedCodes[code.id].checked}
                                mr={2}
                                onChange={e => handleSingleCheckbox(e, code.id)}
                              />
                            </GridItem>
                            <GridItem colSpan={2}>
                              {code.letter}
                            </GridItem>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </HStack>

                <Box>
                  <Box pb={4}>
                    <Box pb={2}>
                      <Text
                        color="greyScale.700"
                        fontSize="sm"
                        pb={2}
                      >
                        Code Application
                      </Text>
                      <HStack
                        justifyContent="space-between"
                      >
                        <Button
                          colorScheme="brand"
                          data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkSuggestApplyCodeButton}
                          leftIcon={<Icon as={BsPlus} />}
                          minW="113px"
                          size="sm"
                          variant="accent"
                          onClick={() => handleApplyCodes(CodeActions.Suggest)}
                        >
                          Suggest
                        </Button>
                        <Authorizer permission={CREATE_ITEM_CODE_STATUS_APPLIED}>
                          <Button
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkApplyCodeButton}
                            leftIcon={<Icon as={BsPlus} />}
                            minW="113px"
                            size="sm"
                            onClick={() => handleApplyCodes(CodeActions.Apply)}
                          >
                            Apply
                          </Button>
                        </Authorizer>
                      </HStack>
                    </Box>

                    <Box>
                      <Text
                        color="greyScale.700"
                        fontSize="sm"
                        py={2}
                      >
                        Code Clearance
                      </Text>
                      <HStack
                        justifyContent="space-between"
                      >
                        <Button
                          colorScheme="blue"
                          data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkSuggestClearCodeButton}
                          leftIcon={<Icon as={BiMinus} />}
                          minW="113px"
                          size="sm"
                          variant="accent"
                          onClick={() => handleApplyCodes(CodeActions.SuggestRemoval)}
                        >
                          Suggest
                        </Button>
                        <Authorizer permission={CREATE_ITEM_CODE_STATUS_APPLIED}>
                          <Button
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkClearCodeButton}
                            leftIcon={<Icon as={BiMinus} />}
                            minW="113px"
                            size="sm"
                            onClick={() => handleApplyCodes(CodeActions.Clear)}
                          >
                            Clear
                          </Button>
                        </Authorizer>
                      </HStack>
                    </Box>
                  </Box>
                  <Authorizer permission={CREATE_ITEM_CODE_STATUS_WAIVED}>
                    <Divider />
                    <Box py={4}>
                      <Box pb={2}>
                        <Text
                          color="greyScale.700"
                          fontSize="sm"
                          pb={2}
                        >
                          Waive Code
                        </Text>
                        <HStack
                          justifyContent="space-between"
                        >
                          <Button
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkWaiveCodeButton}
                            leftIcon={<Icon as={BsFlagFill} />}
                            minW="113px"
                            size="sm"
                            variant="accentCaution"
                            onClick={() => handleApplyCodes(CodeActions.Waive)}
                          >
                            Waive
                          </Button>
                          <Button
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkUnwaiveCodeButton}
                            minW="113px"
                            size="sm"
                            variant="secondaryCaution"
                            onClick={() => handleApplyCodes(BulkCodeActions.Unwaive)}
                          >
                            Unwaive
                          </Button>
                        </HStack>
                      </Box>

                      <Box>
                        <Text
                          color="greyScale.700"
                          fontSize="sm"
                          py={2}
                        >
                          Code Exception
                        </Text>
                        <HStack
                          justifyContent="space-between"
                        >
                          <Button
                            colorScheme="black"
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkApplyExceptionCodeButton}
                            leftIcon={<Icon as={AiFillStar} />}
                            minW="113px"
                            size="sm"
                            variant="accentBlack"
                            onClick={() => handleApplyCodes(CodeActions.Inquire)}
                          >
                            Exception
                          </Button>
                          <Button
                            data-analytics-id={analyticsIds.insuredProfilePage.itemsComponent.bulkClearExceptionCodeButton}
                            minW="113px"
                            size="sm"
                            variant="secondaryBlack"
                            onClick={() => handleApplyCodes(BulkCodeActions.Uninquire)}
                          >
                            Remove
                          </Button>
                        </HStack>
                      </Box>
                    </Box>
                  </Authorizer>
                </Box>
              </HStack>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

ItemCodesBulkUpdatePopover.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired
};

export default ItemCodesBulkUpdatePopover;
